import { z } from "zod";
export const passwordSchema = z
    .string()
    .min(8, "Use at least 8 characters")
    .max(128, "That password is too long");
export const registerInputSchema = z.object({
    email: z.string().trim().toLowerCase().email("Enter a valid email"),
    password: passwordSchema,
    displayName: z
        .string()
        .trim()
        .min(2, "Pick a name with at least 2 characters")
        .max(40, "Keep your name under 40 characters"),
    /// First-touch `document.referrer`, captured client-side (lib/visitor.ts)
    /// — feeds the admin "where users came from" report. Never required.
    referrer: z.string().trim().max(500).optional(),
    /// A friend's invite code (see referral.ts) — typed in or from the link.
    referralCode: z.string().trim().max(20).optional(),
    /// The consent box: the user agreement, the privacy policy and consent to
    /// data processing. No account without it.
    acceptTerms: z.literal(true, { errorMap: () => ({ message: "Примите соглашение и политику конфиденциальности" }) }),
});
export const loginInputSchema = z.object({
    email: z.string().trim().toLowerCase().email("Enter a valid email"),
    password: z.string().min(1, "Enter your password"),
});
/** The payload the Telegram Login Widget hands back — verified server-side
 * via `hash` (HMAC-SHA256 of the rest, keyed by the bot token) before it's
 * trusted for anything. */
export const telegramAuthInputSchema = z.object({
    id: z.number().int().positive(),
    first_name: z.string(),
    last_name: z.string().optional(),
    username: z.string().optional(),
    photo_url: z.string().url().optional(),
    auth_date: z.number().int().positive(),
    hash: z.string(),
});
/** Google Identity Services sends an ID token; the API verifies its signature and claims. */
export const googleAuthInputSchema = z.object({
    credential: z.string().min(100).max(8192),
    acceptTerms: z.boolean().optional(),
    referralCode: z.string().trim().max(20).optional(),
});
export const roleSchema = z.enum(["USER", "ADMIN"]);
export const publicUserSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    displayName: z.string(),
    avatarUrl: z.string().nullable().default(null),
    /** The frame the account wears — drawn on its avatar in the header too. */
    avatarFrame: z.string().nullable().default(null),
    createdAt: z.string(),
    role: roleSchema.default("USER"),
    /// Every account created through normal signup already confirmed a code
    /// before it existed at all (see confirmRegistrationInputSchema below), so
    /// this is `true` from the very first moment it's visible to the client.
    /// Kept as a field (rather than removed) for accounts created before that
    /// was true, and for Telegram sign-in, which has no inbox to confirm.
    emailVerified: z.boolean().default(false),
    /// True for an account created/linked via "Sign in with Telegram" — it has
    /// no real password behind it (see AuthService.loginWithTelegram), so the
    /// frontend skips asking for one anywhere a password would normally be
    /// required to confirm identity (e.g. deleting the account).
    isTelegramLinked: z.boolean().default(false),
    isGoogleLinked: z.boolean().default(false),
    hasPassword: z.boolean().default(true),
});
export const authResponseSchema = z.object({
    token: z.string(),
    user: publicUserSchema,
});
// ---------------------------------------------------------------------------
// Email verification (at signup) and password reset — both a 6-digit code
// emailed to the account, entered back within a short window.
// ---------------------------------------------------------------------------
export const verificationCodeSchema = z
    .string()
    .trim()
    .regex(/^\d{6}$/, "Enter the 6-digit code");
export const verifyEmailInputSchema = z.object({
    code: verificationCodeSchema,
});
export const forgotPasswordInputSchema = z.object({
    email: z.string().trim().toLowerCase().email("Enter a valid email"),
});
export const resetPasswordInputSchema = z.object({
    email: z.string().trim().toLowerCase().email("Enter a valid email"),
    code: verificationCodeSchema,
    password: passwordSchema,
});
// ---------------------------------------------------------------------------
// Signup confirmation — the code sent by `registerInputSchema`'s endpoint.
// No account exists yet at this point (see AuthService.requestRegistration),
// so these carry the email explicitly rather than relying on a bearer token.
// ---------------------------------------------------------------------------
export const confirmRegistrationInputSchema = z.object({
    email: z.string().trim().toLowerCase().email("Enter a valid email"),
    code: verificationCodeSchema,
    /// What /auth/register answered to the browser that sent the details.
    ticket: z.string().max(200).optional(),
    /// Without that ticket (the link opened on another device): the password
    /// chosen at signup, so only its owner can confirm it.
    password: z.string().max(128).optional(),
});
export const resendRegistrationInputSchema = z.object({
    email: z.string().trim().toLowerCase().email("Enter a valid email"),
});
export const deleteAccountInputSchema = z.object({
    /// Empty for a Telegram-only account, which has no real password behind
    /// it (see AuthService.deleteAccount) — the field still has to be present
    /// in the request shape, just permitted to be blank in that one case.
    password: z.string(),
});
//# sourceMappingURL=auth.js.map